import type { ThreadMessage } from '@bcecloud/portal-ui';
import type { Charity } from '@/lib/charities';
import type { ContactType } from '@/lib/contact-types';
import type { EventCsr, EventDetail, EventNote, EventPerson, EventSummary, FamilyMember, Shipment, ShippingView, SystemSettings, Program } from '@/lib/events';
import type { MentionCandidateDto } from '@/lib/internal-messages';
import type { AssistantsView, SimilarPerson, StaffContact } from '@/lib/staffing';
import type { PackingLine, SupplyItem } from '@/lib/supplies';
import type { Template } from '@/lib/templates';
import seedJson from './seed/sandbox.json';
import { PORTAL_SEED_VERSION, portalSeed, type PortalStore } from './seed/portal';

/**
 * The sandbox's data: one fictional client's event and everything around
 * it, loaded from the scrubbed seed and kept in sessionStorage so a reload
 * inside the sandbox keeps what was done and closing the tab forgets it.
 */

export interface SandboxStore {
  version: number;
  eventId: string;
  programId: string;
  eventList: EventSummary[];
  event: EventDetail;
  csr: EventCsr;
  users: EventPerson[];
  family: FamilyMember[];
  packingList: PackingLine[];
  shipments: Shipment[];
  otherShipments: Shipment[];
  shipping: ShippingView;
  assistants: AssistantsView;
  notes: EventNote[];
  messages: ThreadMessage[];
  candidates: MentionCandidateDto[];
  people: SimilarPerson[];
  staffContacts: StaffContact[];
  charities: Charity[];
  templates: Template[];
  contactTypes: ContactType[];
  settings: SystemSettings;
  program: Program;
  supplies: SupplyItem[];
  /** The client portal side: Northwind Group and the content Roy curates. */
  portal: PortalStore;
}

const STORAGE_KEY = 'bce.sandbox.store';

// The seed is written by our own scrub script from the API's typed
// responses; this is the one place its shape is asserted.
const seed = seedJson as unknown as Omit<SandboxStore, 'portal'>;

let current: SandboxStore | null = null;

export function freshStore(): SandboxStore {
  return { ...structuredClone(seed), portal: portalSeed(new Date()) };
}

/** The live store, restored from the session or built fresh from the seed. */
export function getStore(): SandboxStore {
  if (current) return current;
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as SandboxStore;
      if (parsed.version === seed.version && parsed.portal?.version === PORTAL_SEED_VERSION) {
        current = parsed;
        return current;
      }
    }
  } catch {
    // A corrupt or unavailable session falls back to the seed.
  }
  current = freshStore();
  return current;
}

/** Persists the store after a write. */
export function commit(store: SandboxStore): void {
  current = store;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Storage full or blocked: the session keeps working in memory.
  }
}

export function resetStore(): SandboxStore {
  current = freshStore();
  commit(current);
  return current;
}

export function dropStore(): void {
  current = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to drop.
  }
}

export function newId(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function now(): string {
  return new Date().toISOString();
}

export interface PageMeta {
  readonly page: number;
  readonly limit: number;
  readonly total: number;
  readonly totalPages: number;
  readonly hasNext: boolean;
  readonly hasPrev: boolean;
}

/** One page of a list plus the pagination envelope the app expects. */
export function paginate<T>(rows: readonly T[], query: URLSearchParams, defaultLimit = 25): { data: T[]; pagination: PageMeta } {
  const page = Math.max(1, Number(query.get('page') ?? 1) || 1);
  const limit = Math.max(1, Number(query.get('limit') ?? defaultLimit) || defaultLimit);
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  return {
    data: rows.slice((page - 1) * limit, page * limit),
    pagination: { page, limit, total, totalPages, hasNext: page < totalPages, hasPrev: page > 1 },
  };
}

/** Case-insensitive "every word appears somewhere in these fields". */
export function matches(search: string | null, ...fields: ReadonlyArray<string | number | null | undefined>): boolean {
  const words = (search ?? '').toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = fields
    .filter((field): field is string | number => field !== null && field !== undefined)
    .map(String)
    .join(' ')
    .toLowerCase();
  return words.every((word) => haystack.includes(word));
}

/** Sorts a copy by a field, `asc` or `desc`, undefined last. */
export function sortBy<T>(rows: readonly T[], field: string | null, direction: string | null, pick: (row: T, field: string) => string | number | undefined): T[] {
  if (!field) return [...rows];
  const factor = direction === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const left = pick(a, field);
    const right = pick(b, field);
    if (left === undefined) return right === undefined ? 0 : 1;
    if (right === undefined) return -1;
    if (typeof left === 'number' && typeof right === 'number') return (left - right) * factor;
    return String(left).localeCompare(String(right)) * factor;
  });
}
